/**
 * Staff home route — where a staff/admin/manager lands after sign-in
 * and where the navbar logo points.
 *
 * Resolution order (highest precedence first):
 *   1. users.staff_home_route — per-user override (nullable)
 *   2. settings.staff_default_home_route — per-tenant default
 *   3. literal 'dashboard' — compiled-in floor
 *
 * Keep the allow-list in sync with:
 *   - backend src/controllers/userController.js UserController.HOME_ROUTES
 *   - migration tenant/20260519_002_staff_home_route.sql check constraint
 */

export type StaffHomeRoute =
  | "dashboard"
  | "memberships"
  | "athletes"
  | "attendance"
  | "lead_pipeline"
  | "my_tasks";

export interface StaffHomeRouteOption {
  value: StaffHomeRoute;
  label: string;
  description: string;
}

export const STAFF_HOME_ROUTE_OPTIONS: StaffHomeRouteOption[] = [
  { value: "dashboard", label: "Dashboard", description: "Tips, what's new, and quick links" },
  { value: "memberships", label: "Memberships", description: "List of member accounts" },
  { value: "athletes", label: "Athletes", description: "Athlete roster" },
  { value: "attendance", label: "Attendance", description: "Mark attendance for today" },
  { value: "lead_pipeline", label: "Lead Pipeline", description: "CRM leads kanban" },
  { value: "my_tasks", label: "My Tasks", description: "Tasks assigned to you" },
];

const STAFF_HOME_ROUTE_SET = new Set<StaffHomeRoute>(
  STAFF_HOME_ROUTE_OPTIONS.map((o) => o.value),
);

export function isStaffHomeRoute(v: unknown): v is StaffHomeRoute {
  return typeof v === "string" && STAFF_HOME_ROUTE_SET.has(v as StaffHomeRoute);
}

/**
 * Resolve the effective home route for a staff/admin/manager user,
 * applying the user override → tenant default → 'dashboard' precedence.
 * Unknown values (e.g. stale data after the route was removed from the
 * allow-list) fall through to the next level.
 */
export function resolveStaffHomeRoute(
  userRoute: string | null | undefined,
  tenantDefault: string | null | undefined,
): StaffHomeRoute {
  if (isStaffHomeRoute(userRoute)) return userRoute;
  if (isStaffHomeRoute(tenantDefault)) return tenantDefault;
  return "dashboard";
}

/**
 * Convert a resolved home route + tenant slug into a relative URL.
 */
export function staffHomeRoutePath(
  route: StaffHomeRoute,
  tenantId: string,
): string {
  const base = `/${tenantId}`;
  switch (route) {
    case "dashboard":
      return `${base}/manage/dashboard`;
    case "memberships":
      return `${base}/manage/memberships`;
    case "athletes":
      return `${base}/manage/athletes`;
    case "attendance":
      return `${base}/manage/attendance`;
    case "lead_pipeline":
      return `${base}/manage/crm/leads`;
    case "my_tasks":
      return `${base}/manage/tasks`;
  }
}
